import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_ALL_PIPELINES_FULL } from '../../gql/queries/pipelines'
import { GET_ALL_LEAD_SOURCE_CATEGORIES } from '../../gql/queries/leadSourceQuery'
import { GENERATE_PUBLIC_TOKEN } from '../../gql/mutations/publicTokenGenerator'
import { GET_ALL_USERS_LIST } from '../../gql/queries/usersQuery'
import { GET_ALL_SIGNATURE_FORMS_ACTIVE } from '../../gql/queries/signatureFormsApiQueries'
import { parseId } from '../../helpers'
import { ADD_EDIT_LEAD_SOURCES } from '../../gql/mutations/leadSourceMutations'
import { IResAllListUsers } from '../../types/users'
import { IResSignatureFormsAPI } from '../../types/signatureFormsApi'

interface Props {
    show: boolean
    onHide: () => void
}

export const GenerateTokenModal: React.FC<Props> = ({ show, onHide }) => {
    const [stagePipelineId, setStagePipelineId] = useState<string>('')
    const [pipelineId, setPipelineId] = useState<string>('')
    const [leadSourceCategoryId, setLeadSourceCategoryId] = useState<string>('')
    const [leadSourceId, setLeadSourceId] = useState<string>('')
    const [campusId, setCampusId] = useState<string>('1')
    const [stagePipelineIntentId, setStagePipelineIntentId] = useState<string>('')
    const [stagePipelineSignedId, setStagePipelineSignedId] = useState<string>('')
    const [stagePipelineQuotedId, setStagePipelineQuotedId] = useState<string>('')
    const [agentId, setAgentId] = useState<string>('')
    const [signatureFormEsId, setSignatureFormEsId] = useState<string>('')
    const [signatureFormEnId, setSignatureFormEnId] = useState<string>('')
    const [sendEmail, setSendEmail] = useState<boolean>(true)
    const [sendSms, setSendSms] = useState<boolean>(false)
    const [requireIdentity, setRequireIdentity] = useState<boolean>(true)
    const [sourceDescription, setSourceDescription] = useState<string>('')
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false)
    const [generatedToken, setGeneratedToken] = useState<string>('')
    const [useShortUrl, setUseShortUrl] = useState<boolean>(false)
    const [slug, setSlug] = useState<string>('')
    const [phoneNumber, setPhoneNumber] = useState<string>('')
    const [whatsappNumber, setWhatsappNumber] = useState<string>('')

    // Queries
    const { data: pipelinesData, loading: loadingPipelines } = useQuery(GET_ALL_PIPELINES_FULL, {
        skip: !show,
    })

    const { data: sourcesData, loading: loadingSources } = useQuery(GET_ALL_LEAD_SOURCE_CATEGORIES, {
        variables: { first: 100, offset: 0 },
        fetchPolicy: 'no-cache',
        skip: !show,
    })

    const { data: usersData, loading: loadingUsers } = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {
        skip: !show,
    })

    const { data: signatureFormsData, loading: loadingSignatureForms } = useQuery<IResSignatureFormsAPI>(
        GET_ALL_SIGNATURE_FORMS_ACTIVE,
        {
            variables: { first: 100 },
            skip: !show,
        }
    )

    const [generateToken, { loading: generating }] = useMutation(GENERATE_PUBLIC_TOKEN)
    const [updateLeadSource, { loading: updatingSource }] = useMutation(ADD_EDIT_LEAD_SOURCES)

    const pipelines = pipelinesData?.allPipelinesFull?.edges?.map((e: any) => e.node) || []
    const categories = sourcesData?.allCategoryLeadSource?.edges?.map((e: any) => e.node) || []
    const users = usersData?.allUsersList || []
    const signatureForms = signatureFormsData?.allSignatureForms?.edges?.map((e: any) => e.node) || []

    // Derived stages based on selected pipeline
    const selectedPipeline = pipelines.find((p: any) => p.id === pipelineId)
    const stages = selectedPipeline?.stagePipelineSet?.edges?.map((e: any) => e.node) || []

    // Derived sources based on selected category
    const selectedCategory = categories.find((c: any) => c.id === leadSourceCategoryId)
    const filteredSources = selectedCategory?.leadSourceSet?.edges?.map((e: any) => e.node).filter((s: any) => s.enabled !== false) || []

    const selectedLeadSource = filteredSources.find((s: any) => s.id === leadSourceId)

    useEffect(() => {
        if (selectedLeadSource) {
            setSlug(selectedLeadSource.slug || '')
            if (selectedLeadSource.metadata) {
                try {
                    const meta = typeof selectedLeadSource.metadata === 'string' 
                        ? JSON.parse(selectedLeadSource.metadata) 
                        : selectedLeadSource.metadata
                    setPhoneNumber(meta.phoneNumber || '')
                    setWhatsappNumber(meta.whatsappNumber || '')
                } catch (e) {
                    console.error('Error parsing metadata', e)
                }
            } else {
                setPhoneNumber('')
                setWhatsappNumber('')
            }
        }
    }, [leadSourceId, selectedLeadSource])

    useEffect(() => {
        if (!show) {
            setPipelineId('')
            setStagePipelineId('')
            setLeadSourceCategoryId('')
            setLeadSourceId('')
            setCampusId('1')
            setStagePipelineIntentId('')
            setStagePipelineSignedId('')
            setStagePipelineQuotedId('')
            setAgentId('')
            setSignatureFormEsId('')
            setSignatureFormEnId('')
            setSendEmail(true)
            setSendSms(false)
            setRequireIdentity(true)
            setSourceDescription('')
            setShowAdvanced(false)
            setGeneratedToken('')
            setUseShortUrl(false)
            setSlug('')
            setPhoneNumber('')
            setWhatsappNumber('')
        }
    }, [show])

    const handleGenerate = async () => {
        if (!stagePipelineId || !leadSourceId || !campusId) {
            toast.error('Please fill all the required fields')
            return
        }

        try {
            const { data } = await generateToken({
                variables: {
                    leadSourceId: Number(parseId(leadSourceId)),
                    stagePipelineId: Number(parseId(stagePipelineId)),
                    campusId: Number(campusId),
                    stagePipelineIntentId: stagePipelineIntentId ? Number(parseId(stagePipelineIntentId)) : null,
                    stagePipelineSignedId: stagePipelineSignedId ? Number(parseId(stagePipelineSignedId)) : null,
                    stagePipelineQuotedId: stagePipelineQuotedId ? Number(parseId(stagePipelineQuotedId)) : null,
                    agentId: agentId ? Number(agentId) : null,
                    signatureFormEsId: signatureFormEsId ? Number(signatureFormEsId) : null,
                    signatureFormEnId: signatureFormEnId ? Number(signatureFormEnId) : null,
                    sendEmail,
                    sendSms,
                    requireIdentity,
                    sourceDescription: sourceDescription || null,
                },
            })

            if (data?.generatePublicToken?.success) {
                setGeneratedToken(data.generatePublicToken.token)
                toast.success('Token generated successfully')
            } else {
                const errorMsg = data?.generatePublicToken?.errors?.[0]?.message || 'Failed to generate token'
                toast.error(errorMsg)
            }
        } catch (e: any) {
            toast.error(e.message || 'An error occurred')
        }
    }

    const handleSaveAndGenerateSlug = async () => {
        if (!slug || !leadSourceId) {
            toast.error('Slug and Lead Source are required for short URL')
            return
        }

        try {
            const metadata = {
                phoneNumber,
                whatsappNumber
            }

            const { data } = await updateLeadSource({
                variables: {
                    input: {
                        id: parseId(leadSourceId),
                        name: selectedLeadSource.name,
                        category: parseId(selectedLeadSource.category.id),
                        slug: slug,
                        metadata: JSON.stringify(metadata)
                    }
                }
            })

            if (data?.leadSourceCreateupdateMutation?.leadSource) {
                toast.success('Campaign configuration saved')
                const lang = 'en' // Default or based on state
                const baseUrl = 'https://quote.thefutureinsurance.com' // Should be dynamic
                setGeneratedToken(`${baseUrl}/${lang}/${slug}`)
            } else {
                toast.error('Failed to update lead source')
            }
        } catch (e: any) {
            toast.error(e.message || 'Error saving campaign config')
        }
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedToken)
        toast.success('Token copied to clipboard')
    }

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Generate Lovable Campaign Token</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <Form.Label>Pipeline</Form.Label>
                    <Form.Select
                        value={pipelineId}
                        onChange={(e) => {
                            setPipelineId(e.target.value)
                            setStagePipelineId('')
                            setGeneratedToken('')
                        }}
                        disabled={loadingPipelines}
                    >
                        <option value="">Select Pipeline...</option>
                        {pipelines.map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-3">
                    <Form.Label>Stage Pipeline (Required)</Form.Label>
                    <Form.Select
                        value={stagePipelineId}
                        onChange={(e) => {
                            setStagePipelineId(e.target.value)
                            setGeneratedToken('')
                        }}
                        disabled={!pipelineId || stages.length === 0}
                    >
                        <option value="">Select Stage...</option>
                        {stages.map((s: any) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-3">
                    <Form.Label>Lead Source Category (Required)</Form.Label>
                    <Form.Select
                        value={leadSourceCategoryId}
                        onChange={(e) => {
                            setLeadSourceCategoryId(e.target.value)
                            setLeadSourceId('')
                            setGeneratedToken('')
                        }}
                        disabled={loadingSources}
                    >
                        <option value="">Select Category...</option>
                        {categories.map((cat: any) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-3">
                    <Form.Label>Lead Source (Required)</Form.Label>
                    <Form.Select
                        value={leadSourceId}
                        onChange={(e) => {
                            setLeadSourceId(e.target.value)
                            setGeneratedToken('')
                        }}
                        disabled={!leadSourceCategoryId || filteredSources.length === 0}
                    >
                        <option value="">Select Lead Source...</option>
                        {filteredSources.map((s: any) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-4 border p-3 rounded bg-light">
                    <Form.Check
                        type="switch"
                        id="use-short-url"
                        label="Use Short URL (Slug Based)"
                        checked={useShortUrl}
                        onChange={(e) => {
                            setUseShortUrl(e.target.checked)
                            setGeneratedToken('')
                        }}
                    />
                    {useShortUrl && (
                        <div className="mt-3">
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">URL Slug</Form.Label>
                                <Form.Control 
                                    size="sm" 
                                    type="text" 
                                    placeholder="agent-campaign-name" 
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Custom Phone</Form.Label>
                                <Form.Control 
                                    size="sm" 
                                    type="text" 
                                    placeholder="+1 800-000-0000" 
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-0">
                                <Form.Label className="small fw-bold">Custom WhatsApp</Form.Label>
                                <Form.Control 
                                    size="sm" 
                                    type="text" 
                                    placeholder="18000000000" 
                                    value={whatsappNumber}
                                    onChange={(e) => setWhatsappNumber(e.target.value)}
                                />
                            </Form.Group>
                        </div>
                    )}
                </div>

                <div className="mb-4">
                    <Form.Label>Campus ID (Optional, Default is 1)</Form.Label>
                    <Form.Control
                        type="number"
                        value={campusId}
                        onChange={(e) => {
                            setCampusId(e.target.value)
                            setGeneratedToken('')
                        }}
                    />
                </div>

                <div className="mb-3">
                    <Button
                        variant="light"
                        className="w-100 text-start fw-bold"
                        onClick={() => setShowAdvanced((prev) => !prev)}
                    >
                        {showAdvanced ? 'Hide advanced fields' : 'Show advanced fields'}
                    </Button>
                </div>

                {showAdvanced && (
                    <>
                        <div className="mb-3">
                            <Form.Label>Stage Pipeline Intent ID (Optional)</Form.Label>
                            <Form.Select value={stagePipelineIntentId} onChange={(e) => setStagePipelineIntentId(e.target.value)} disabled={!pipelineId || stages.length === 0}>
                                <option value="">Select Stage...</option>
                                {stages.map((s: any) => (
                                    <option key={`intent-${s.id}`} value={s.id}>{s.name}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="mb-3">
                            <Form.Label>Stage Pipeline Signed ID (Optional)</Form.Label>
                            <Form.Select value={stagePipelineSignedId} onChange={(e) => setStagePipelineSignedId(e.target.value)} disabled={!pipelineId || stages.length === 0}>
                                <option value="">Select Stage...</option>
                                {stages.map((s: any) => (
                                    <option key={`signed-${s.id}`} value={s.id}>{s.name}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="mb-3">
                            <Form.Label>Stage Pipeline Quoted ID (Optional)</Form.Label>
                            <Form.Select value={stagePipelineQuotedId} onChange={(e) => setStagePipelineQuotedId(e.target.value)} disabled={!pipelineId || stages.length === 0}>
                                <option value="">Select Stage...</option>
                                {stages.map((s: any) => (
                                    <option key={`quoted-${s.id}`} value={s.id}>{s.name}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="mb-3">
                            <Form.Label>Select Agent (Optional)</Form.Label>
                            <Form.Select
                                value={agentId}
                                onChange={(e) => setAgentId(e.target.value)}
                                disabled={loadingUsers}
                            >
                                <option value="">Select Agent...</option>
                                {users.map((user) => (
                                    <option key={user.id} value={parseId(user.id)}>
                                        {user.firstName} {user.lastName} ({user.primaryGroup?.name || 'No group'})
                                    </option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="mb-3">
                            <Form.Label>Select Signature Form ES (Optional)</Form.Label>
                            <Form.Select
                                value={signatureFormEsId}
                                onChange={(e) => setSignatureFormEsId(e.target.value)}
                                disabled={loadingSignatureForms}
                            >
                                <option value="">Select Signature Form...</option>
                                {signatureForms.map((form: any) => (
                                    <option key={`es-${form.id}`} value={parseId(form.id)}>
                                        {form.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="mb-3">
                            <Form.Label>Select Signature Form EN (Optional)</Form.Label>
                            <Form.Select
                                value={signatureFormEnId}
                                onChange={(e) => setSignatureFormEnId(e.target.value)}
                                disabled={loadingSignatureForms}
                            >
                                <option value="">Select Signature Form...</option>
                                {signatureForms.map((form: any) => (
                                    <option key={`en-${form.id}`} value={parseId(form.id)}>
                                        {form.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="mb-4">
                            <Form.Label>Source Description (Optional)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="quote.thefutureinsurance.com"
                                value={sourceDescription}
                                onChange={(e) => setSourceDescription(e.target.value)}
                            />
                        </div>

                        <div className="mb-4">
                            <Form.Check
                                type="switch"
                                id="send-email"
                                label="Send Email"
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}
                            />
                            <Form.Check
                                type="switch"
                                id="send-sms"
                                label="Send SMS"
                                checked={sendSms}
                                onChange={(e) => setSendSms(e.target.checked)}
                            />
                            <Form.Check
                                type="switch"
                                id="require-identity"
                                label="Require Identity"
                                checked={requireIdentity}
                                onChange={(e) => setRequireIdentity(e.target.checked)}
                            />
                        </div>
                    </>
                )}

                {generatedToken && (
                    <div className="alert alert-success d-flex flex-column align-items-center mb-0">
                        <h6 className="mb-2 w-100 text-center fw-bold">Your Generated Token</h6>
                        <div
                            className="bg-white p-2 border rounded text-break w-100 mb-3"
                            style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '13px', fontFamily: 'monospace' }}
                        >
                            {generatedToken}
                        </div>
                        <Button variant="success" size="sm" onClick={handleCopy} className="w-100 fw-bold">
                            <i className="bi bi-clipboard me-2"></i>Copy Token
                        </Button>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={useShortUrl ? handleSaveAndGenerateSlug : handleGenerate}
                    disabled={generating || updatingSource || (!useShortUrl && (!stagePipelineId || !leadSourceId)) || (useShortUrl && !slug)}
                >
                    {generating || updatingSource ? <Spinner animation="border" size="sm" /> : (useShortUrl ? 'Save & Generate URL' : 'Generate Token')}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
